import type { ReactNode } from "react";
import { useId, useRef, useState } from "react";
import { Callout } from "ancestry-ui-react";
import { useTranslation } from "react-i18next";
import { invariant } from "@searchui-searchforms/shared/utils";
import { ExactnessButton } from "./ExactnessButton";
import styles from "./ExactnessMultiSelect.module.scss";

interface ExactnessOption<OptionId> {
    id: OptionId;
    label: string;
}

interface ExactnessValue<OptionId> {
    isExact: boolean;
    selectedOptionIds: OptionId[];
}

interface ExactnessMultiSelectProps<OptionId> {
    value: ExactnessValue<OptionId>;
    options: ExactnessOption<OptionId>[];
    onChange: (value: ExactnessValue<OptionId>) => void;
    label: string;
    testId: string;
    footer?: ReactNode;
}

export const ExactnessMultiSelect = <OptionId extends string>({
    value,
    options,
    onChange,
    label,
    testId,
    footer,
}: ExactnessMultiSelectProps<OptionId>) => {
    invariant(options.length >= 2, "At least 2 options are required");
    const [isOpen, setIsOpen] = useState(false);

    const { t } = useTranslation();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const exactnessCheckboxId = useId();

    const getOptionId = (id: string) => `${exactnessCheckboxId}_option_${id}`;
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape") {
            setIsOpen(false);
            triggerRef.current?.focus();
        }
    };

    return (
        <>
            <ExactnessButton
                ref={triggerRef}
                isExact={value.isExact}
                onClick={() => {
                    if (value.isExact) return;
                    onChange({
                        isExact: true,
                        selectedOptionIds: [] as OptionId[],
                    });
                }}
                testId={`${testId}-trigger`}
            >
                {label}
            </ExactnessButton>
            <div className="calloutContainer">
                <Callout
                    trigger={triggerRef}
                    open={isOpen}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => setIsOpen(false)}
                >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <div data-testid={`${testId}-content`} onKeyDown={handleKeyDown}>
                        <div>
                            <input
                                id={exactnessCheckboxId}
                                className="checkbox"
                                type="checkbox"
                                onChange={(event) => {
                                    onChange({
                                        isExact: event.target.checked,
                                        selectedOptionIds: [],
                                    });
                                }}
                                checked={value.isExact}
                                data-testid={`${testId}-exact-checkbox`}
                            />
                            <label htmlFor={exactnessCheckboxId}>{t("exactAnd")}</label>
                        </div>
                        {options.map((option) => (
                            <div key={option.id} className={styles.optionPadding}>
                                <input
                                    className="checkbox"
                                    type="checkbox"
                                    id={getOptionId(option.id)}
                                    value={option.id}
                                    checked={value.selectedOptionIds.includes(option.id)}
                                    onChange={(event) => {
                                        const selected = value.selectedOptionIds.includes(option.id)
                                            ? value.selectedOptionIds.filter((id) => id !== option.id)
                                            : [...value.selectedOptionIds, option.id];

                                        onChange({
                                            ...value,
                                            isExact: event.target.checked ? true : selected.length > 0 || value.isExact,
                                            selectedOptionIds: selected,
                                        });
                                    }}
                                    data-testid={`${testId}-option-${option.id}`}
                                />
                                <label htmlFor={getOptionId(option.id)}>{option.label}</label>
                            </div>
                        ))}
                        {footer}
                    </div>
                </Callout>
            </div>
        </>
    );
};
